import { collection, getDocs, limit, query, where } from "firebase/firestore";
import { db } from "../Context/firebaseConfig";

function endpoint() {
  return String(process.env.REACT_APP_ENDPOINT || "").replace(/\/$/, "");
}

function normalizeRole(value) {
  const role = String(value || "").trim().toLowerCase();
  if (role === "editor") return "Editor";
  if (role === "clipper") return "Clipper";
  if (role === "creator") return "Creator";
  return value || "";
}

function roleFallbacks(role) {
  const normalized = normalizeRole(role);
  if (String(normalized).toLowerCase() === "editor") {
    return ["Editor", "editor", "Clipper", "clipper"];
  }
  if (String(normalized).toLowerCase() === "creator") {
    return ["Creator", "creator"];
  }
  if (normalized) return [normalized];
  return [];
}

function copyFields(source, fields) {
  if (!source || typeof source !== "object") return {};
  return fields.reduce((result, field) => {
    if (source[field] !== undefined && source[field] !== null) {
      result[field] = source[field];
    }
    return result;
  }, {});
}

function buildClientPublicProfile(id, data = {}) {
  const profileInfo = copyFields(data.profileInfo, [
    "username",
    "role",
    "mainGame",
    "contentFocus",
    "about",
    "tagline",
    "profilePicUrl",
    "questions",
    "availableForAll",
    "profileTheme",
    "profileBannerUrl",
  ]);

  profileInfo.questions = copyFields(data.profileInfo?.questions, [
    "questionOne",
    "questionTwo",
    "questionThree",
    "questionFour",
  ]);

  if (typeof profileInfo.mainGame === "object") {
    profileInfo.mainGame = copyFields(profileInfo.mainGame, ["categoryName", "name"]);
  }

  return {
    id,
    profileInfo,
    socials: copyFields(data.socials, ["youtube", "twitch", "instagram", "twitter", "tiktok"]),
    videoLinks: data.videoLinks || {},
    publicPresentation: {
      featuredPlacement: Boolean(data.entitlements?.featuredPlacement),
      homepageSpotlight: Boolean(data.entitlements?.homepageSpotlight),
      higherFeaturedRotationPriority: Boolean(data.entitlements?.higherFeaturedRotationPriority),
      premiumThemes: Boolean(data.entitlements?.premiumThemes),
      customProfileBanner: Boolean(data.entitlements?.customProfileBanner),
      priorityCreatorProfile: Boolean(data.entitlements?.priorityCreatorProfile),
    },
  };
}

async function listPublicProfilesFromFirestore({ role, username, mainGame } = {}) {
  const userCollection = collection(db, "Users");
  const roles = roleFallbacks(role);
  const snapshots = [];

  if (username) {
    snapshots.push(await getDocs(query(userCollection, where("profileInfo.username", "==", String(username).trim()), limit(100))));
  } else if (roles.length > 0) {
    for (const roleValue of roles) {
      snapshots.push(await getDocs(query(userCollection, where("profileInfo.role", "==", roleValue), limit(100))));
    }
  } else {
    snapshots.push(await getDocs(query(userCollection, limit(100))));
  }

  const normalizedGame = String(mainGame || "").trim().toLowerCase();
  const profiles = snapshots
    .flatMap((snapshot) => snapshot.docs.map((document) => buildClientPublicProfile(document.id, document.data())))
    .filter((profile) => {
      if (!normalizedGame) return true;
      const selectedGame = typeof profile.profileInfo.mainGame === "object"
        ? profile.profileInfo.mainGame.categoryName || profile.profileInfo.mainGame.name
        : profile.profileInfo.mainGame;
      return profile.profileInfo.availableForAll === true ||
        String(selectedGame || "").trim().toLowerCase() === normalizedGame;
    });

  return Array.from(new Map(profiles.map((profile) => [profile.id, profile])).values());
}

export async function listPublicProfiles({ role, username, mainGame } = {}) {
  const baseUrl = endpoint();
  if (!baseUrl) return listPublicProfilesFromFirestore({ role, username, mainGame });
  const params = new URLSearchParams();
  if (role) params.set("role", role);
  if (username) params.set("username", username);
  if (mainGame) params.set("mainGame", mainGame);

  try {
    const response = await fetch(`${baseUrl}/api/public-profiles?${params.toString()}`);
    const data = await response.json().catch(() => ({}));
    if (!response.ok) throw new Error(data.error || "Public profiles could not be loaded.");
    const profiles = data.profiles || [];
    if (profiles.length > 0 || username) return profiles;
  } catch (error) {
    console.warn("Public profile API unavailable, falling back to Firestore:", error);
  }

  return listPublicProfilesFromFirestore({ role, username, mainGame });
}

export async function getPublicProfileById(userId) {
  const baseUrl = endpoint();
  if (!baseUrl) throw new Error("Public profile endpoint is not configured.");
  const response = await fetch(`${baseUrl}/api/public-profiles/${encodeURIComponent(userId)}`);
  const data = await response.json().catch(() => ({}));
  if (!response.ok) throw new Error(data.error || "Public profile could not be loaded.");
  return data.profile;
}

export async function recordPublicProfileView(userId) {
  const baseUrl = endpoint();
  if (!baseUrl || !userId) return;
  const response = await fetch(`${baseUrl}/api/public-profiles/${encodeURIComponent(userId)}/view`, {
    method: "POST",
  });
  if (!response.ok) throw new Error("Profile view could not be recorded.");
}
